"use client";

import { c as _c } from "react/compiler-runtime";
import { IconLoader2 } from "@tabler/icons-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trackRollQuantitySelected, trackStackQuantitySelected } from "@/lib/analytics/segment";
import { cn } from "@/lib/utils";
import { PackagingMode } from "@/utils/resolvePackagingMode";
import { FEWEST_ROLLS_LABEL, FEWEST_ROLLS_SELECTION, FEWEST_ROLLS_VALUE, NO_PREFERENCE_LABEL, NO_PREFERENCE_VALUE, offersFewestRolls, type RollDriver, type RollDriverSelection, rollSelectValue, selectionFromOption, toRollOptionValue } from "@/utils/rollQuantityUtils";
import PackagingQuantityOption, { packagingQuantitySelectItemClass, packagingQuantitySelectTriggerClass } from "../ProductBuilder/PackagingQuantityOption";
import type { CartPackagingOption } from "./cartPackagingUtils";
export interface Props {
  mode: PackagingMode;
  rollDriver: RollDriver;
  options: CartPackagingOption[];
  selectedOption: CartPackagingOption | null;
  isRollSelectionLost?: boolean;
  isLoading: boolean;
  onValueChange: (selection: RollDriverSelection) => void;
  labelId?: string;
  errorId?: string;
}
function toValue(option: CartPackagingOption) {
  return toRollOptionValue(option.rollCount, option.rollQty);
}
function trackSelection(mode: PackagingMode, selection: CartPackagingOption) {
  if (mode === PackagingMode.Bundle) {
    trackStackQuantitySelected({
      rollCount: selection.rollCount,
      rollQty: selection.rollQty
    }, "cart");
    return;
  }
  trackRollQuantitySelected({
    numRolls: selection.rollCount,
    itemsPerRoll: selection.rollQty,
    outsideDiameter: selection.diameterInches === undefined ? undefined : `${selection.diameterInches}"`
  }, "cart");
}
export function PackagingQuantitySelect(t0) {
  const $ = _c(46);
  const {
    mode,
    rollDriver,
    options,
    selectedOption,
    isRollSelectionLost,
    isLoading,
    onValueChange,
    labelId,
    errorId
  } = t0;
  let t1;
  if ($[0] !== mode || $[1] !== onValueChange || $[2] !== options || $[3] !== rollDriver) {
    t1 = function handleValueChange(selectedValue) {
      if (selectedValue === NO_PREFERENCE_VALUE) {
        onValueChange(selectionFromOption(rollDriver, {
          rollCount: null,
          rollQty: null
        }));
        trackRollQuantitySelected({
          numRolls: null,
          itemsPerRoll: null
        }, "cart");
        return;
      }
      if (selectedValue === FEWEST_ROLLS_VALUE) {
        onValueChange(FEWEST_ROLLS_SELECTION);
        trackRollQuantitySelected({
          numRolls: FEWEST_ROLLS_SELECTION.rollCount,
          itemsPerRoll: null
        }, "cart");
        return;
      }
      const selected = options.find(option => toValue(option) === selectedValue);
      if (!selected) {
        return;
      }
      onValueChange(selectionFromOption(rollDriver, selected));
      trackSelection(mode, selected);
    };
    $[0] = mode;
    $[1] = onValueChange;
    $[2] = options;
    $[3] = rollDriver;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleValueChange = t1;
  let t2;
  if ($[5] !== mode || $[6] !== rollDriver) {
    t2 = mode === PackagingMode.Roll && offersFewestRolls(rollDriver);
    $[5] = mode;
    $[6] = rollDriver;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const showFewestRolls = t2;
  if (isLoading) {
    let t3;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="flex items-center justify-center py-2"><IconLoader2 className="animate-spin" /></div>;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  if (mode === PackagingMode.Roll && rollDriver === null) {
    let t3;
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = cn("w-full", packagingQuantitySelectTriggerClass);
      t4 = <SelectValue />;
      $[9] = t3;
      $[10] = t4;
    } else {
      t3 = $[9];
      t4 = $[10];
    }
    let t5;
    if ($[11] !== labelId) {
      t5 = <SelectTrigger aria-labelledby={labelId} className={t3}>{t4}</SelectTrigger>;
      $[11] = labelId;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <SelectContent><SelectItem value={NO_PREFERENCE_VALUE}>{NO_PREFERENCE_LABEL}</SelectItem></SelectContent>;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== t5) {
      t7 = <Select value={NO_PREFERENCE_VALUE} disabled={true}>{t5}{t6}</Select>;
      $[14] = t5;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    return t7;
  }
  const t3 = !isRollSelectionLost;
  let t4;
  if ($[16] !== rollDriver || $[17] !== selectedOption || $[18] !== t3) {
    t4 = rollSelectValue(rollDriver, selectedOption, t3);
    $[16] = rollDriver;
    $[17] = selectedOption;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const t5 = errorId ? true : undefined;
  let t6;
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = cn("w-full", packagingQuantitySelectTriggerClass);
    t7 = <SelectValue placeholder="-- Select Roll Quantity --" />;
    $[20] = t6;
    $[21] = t7;
  } else {
    t6 = $[20];
    t7 = $[21];
  }
  let t8;
  if ($[22] !== errorId || $[23] !== labelId || $[24] !== t5) {
    t8 = <SelectTrigger aria-labelledby={labelId} aria-describedby={errorId} aria-invalid={t5} className={t6}>{t7}</SelectTrigger>;
    $[22] = errorId;
    $[23] = labelId;
    $[24] = t5;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== mode) {
    t9 = mode === PackagingMode.Roll && <SelectItem value={NO_PREFERENCE_VALUE}>{NO_PREFERENCE_LABEL}</SelectItem>;
    $[26] = mode;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== showFewestRolls) {
    t10 = showFewestRolls && <SelectItem value={FEWEST_ROLLS_VALUE}>{FEWEST_ROLLS_LABEL}</SelectItem>;
    $[28] = showFewestRolls;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== mode || $[31] !== options || $[32] !== rollDriver) {
    let t12;
    if ($[34] !== mode || $[35] !== rollDriver) {
      t12 = option_0 => <SelectItem className={packagingQuantitySelectItemClass} key={toValue(option_0)} value={toValue(option_0)}><PackagingQuantityOption packageCount={option_0.rollCount} quantityPerPackage={option_0.rollQty} packagingMode={mode} rollDriver={rollDriver} /></SelectItem>;
      $[34] = mode;
      $[35] = rollDriver;
      $[36] = t12;
    } else {
      t12 = $[36];
    }
    t11 = options.map(t12);
    $[30] = mode;
    $[31] = options;
    $[32] = rollDriver;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t9) {
    t12 = <SelectContent>{t9}{t10}{t11}</SelectContent>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t9;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== handleValueChange || $[42] !== t12 || $[43] !== t4 || $[44] !== t8) {
    t13 = <Select value={t4} onValueChange={handleValueChange}>{t8}{t12}</Select>;
    $[41] = handleValueChange;
    $[42] = t12;
    $[43] = t4;
    $[44] = t8;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  return t13;
}